import { useOrg } from '@/organization/useOrg';
import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  SubprojectBuildingDetails,
  TechnologyParameter,
  useSubprojectStore,
} from '../../stores/SubprojectStore.ts';
import { DecarbFooterBar } from '../components/DecarbFooterBar.tsx';
import { DecarbPageLayout } from '../components/DecarbPageLayout.tsx';
import { DecarbTabsPanel } from '../components/DecarbTabsPanel.tsx';
import { HelperText } from '../components/HelperText.tsx';
import { usePencilsDown } from '../hooks/usePencilsDown.ts';
import { useLightingWithMetrics } from '../queries/lighting';
import { safelySaveSubproject } from '../saveHelpers.ts';
import { Tab, TabKinds } from '../types';
import {
  categorySelectionsMatch,
  createEmptyBuildingAlternative,
  createEmptySubprojectTechnology,
  storedCategoryCapitalCost,
  updateTechParams,
} from '../utils.ts';
import { LightingForBuildingDetails } from './LightingForBuildingDetails.tsx';

// Ensure every alternative has lighting tech params, on a clone, before it
// becomes state — mutating state in place later skips re-renders.
const withLightingDefaults = (
  subproject: SubprojectBuildingDetails,
): SubprojectBuildingDetails => {
  const updated = structuredClone(subproject);
  for (const alternativeTab of TabKinds) {
    if (!updated[alternativeTab]) {
      updated[alternativeTab] = createEmptyBuildingAlternative();
    }
    // stored alternatives have shipped without this field before
    updated[alternativeTab].technologyParams ??= {};
    if (!updated[alternativeTab].technologyParams['lighting']) {
      updated[alternativeTab].technologyParams['lighting'] =
        createEmptySubprojectTechnology('lighting');
    }
  }
  return updated;
};

export const LightingSelection = () => {
  const pencilsDown = usePencilsDown();
  const queryClient = useQueryClient();
  const subprojectStore = useSubprojectStore();
  const { subId } = useParams();
  const { selectedOrg } = useOrg();
  const [subproject, setSubproject] = useState<SubprojectBuildingDetails>();
  const [tab, setTab] = useState<Tab>('conventionalAlternative');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // The copy last handed to us by the server (on load, or as a save's result).
  // Identity against `subproject` says whether there is anything to save;
  // `costsArePending` says whether the stored cost figures still describe what
  // is on screen. Same pattern as HVACSelection.
  const [syncedSubproject, setSyncedSubproject] =
    useState<SubprojectBuildingDetails>();
  const costsArePending = !categorySelectionsMatch(
    subproject,
    syncedSubproject,
    'lighting',
  );
  // Incremented as each save request is issued, so a response can tell
  // whether it is still the newest one outstanding.
  const saveSeqRef = useRef(0);

  /**
   * Adopt what the backend handed back from a save: the stored costs only
   * exist after the server has run. Ignored if a newer save has been issued
   * since (out-of-order completion); the on-screen state is replaced only if
   * no edit was made while the save was in flight. See HVACSelection for the
   * full reasoning.
   */
  const adoptSaveResult = useCallback(
    (
      savedSnapshot: SubprojectBuildingDetails,
      result: SubprojectBuildingDetails,
      seq: number,
    ) => {
      if (seq !== saveSeqRef.current) return;
      const refreshed = withLightingDefaults(result);
      setSyncedSubproject(refreshed);
      setSubproject((current) =>
        current === savedSnapshot ? refreshed : current,
      );
    },
    [],
  );

  const { data: lightingSystems } = useLightingWithMetrics(
    subproject?.conventionalAlternative?.buildingParams?.conditionedSpace,
  );

  // Fetch subproject from database and initialize alternatives if needed
  useEffect(() => {
    if (!subId || !selectedOrg) {
      return;
    }

    subprojectStore
      .fetchSubprojectDetails(subId, selectedOrg.id)
      .then((subproject) => {
        const updatedSubproject = withLightingDefaults(subproject);
        setSubproject(updatedSubproject);
        setSyncedSubproject(updatedSubproject);
        return updatedSubproject;
      })
      .catch(console.error);
  }, [subId, subprojectStore, selectedOrg]);

  // Debounced auto-save after selection changes
  useEffect(() => {
    if (pencilsDown) return;
    if (!subproject || !subId || !selectedOrg) return;

    // Nothing has changed since the server last spoke: covers the initial load
    // and a state adopted from a save result (saving that would loop).
    if (subproject === syncedSubproject) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const savedSnapshot = subproject;
    saveTimeoutRef.current = setTimeout(() => {
      setIsSaving(true);
      const seq = ++saveSeqRef.current;
      safelySaveSubproject(subId, selectedOrg.id, savedSnapshot, queryClient)
        .then((result) => adoptSaveResult(savedSnapshot, result, seq))
        .catch(console.error)
        .finally(() => setIsSaving(false));
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    pencilsDown,
    subproject,
    syncedSubproject,
    subId,
    selectedOrg,
    queryClient,
    adoptSaveResult,
  ]);

  // Handle increment, decrement and change of lighting counts
  const handleChangeLightingCount = useCallback(
    (lightingId: string, newCount: number) => {
      setSubproject((previousSubproject) => {
        if (!previousSubproject) {
          return;
        }

        if (
          !previousSubproject?.[tab]?.technologyParams?.['lighting']?.params
        ) {
          return;
        }

        // Find the lighting technology to check its limit
        const lighting = lightingSystems?.find(
          (l) => String(l.id) === lightingId,
        );
        if (!lighting) {
          return;
        }

        return updateTechParams(
          previousSubproject,
          tab,
          'lighting',
          (params) => {
            if (newCount > 0) {
              return params.map((lightingSystem) =>
                lightingSystem.techId === lightingId
                  ? { ...lightingSystem, units: newCount }
                  : lightingSystem,
              );
            } else {
              return params.filter(
                (lightingSystem: TechnologyParameter) =>
                  lightingSystem.techId !== lightingId,
              );
            }
          },
        );
      });
    },
    [tab, lightingSystems],
  );

  // Add a lighting system to list of selected lighting systems
  const handleAddLighting = useCallback(
    (lightingId: string, limit: number) => {
      setSubproject((previousSubproject) => {
        if (!previousSubproject) {
          return;
        }

        if (
          !previousSubproject?.[tab]?.technologyParams?.['lighting']?.params
        ) {
          return;
        }

        return updateTechParams(
          previousSubproject,
          tab,
          'lighting',
          (params) => [...params, { techId: lightingId, units: limit }],
        );
      });
    },
    [tab],
  );

  // Copy selected lighting systems from the previous alternative
  const handleCopyLightingSettings = useCallback(
    (tab: Tab) => {
      setSubproject((previousSubproject) => {
        if (!previousSubproject || !lightingSystems) {
          return;
        }

        // Don't copy if already on conventional
        if (tab === 'conventionalAlternative') {
          return;
        }

        // Determine which alternative to copy FROM
        let tabToCopyFrom: Tab = 'conventionalAlternative';
        if (tab === 'zeroCarbonAlternative') {
          tabToCopyFrom = 'reducedCarbonAlternative';
        }

        const lightingList: TechnologyParameter[] =
          previousSubproject[tabToCopyFrom].technologyParams['lighting']
            ?.params || [];

        if (lightingList.length === 0) {
          return;
        }

        // Filter lighting systems based on target alternative
        const filteredLightingList = lightingList.filter((lightingParam) => {
          const lighting = lightingSystems.find(
            (l) => String(l.id) === lightingParam.techId,
          );
          if (!lighting) {
            console.error(
              'Unable to locate lighting unit during copy, not found in storage',
            );
            return false;
          }

          // Low Carbon: No gas
          if (tab === 'reducedCarbonAlternative') {
            if (lighting.energySink?.includes('Gas')) {
              return false;
            }
          }

          // Net Zero: No gas AND only low-energy electricity (≤2 icons)
          if (tab === 'zeroCarbonAlternative') {
            if (lighting.energySink?.includes('Gas')) {
              return false;
            }
            if (
              lighting.energySink === 'Electricity' &&
              lighting.relativeEnergyDemand > 2
            ) {
              return false;
            }
          }

          return true;
        });

        const excludedCount = lightingList.length - filteredLightingList.length;
        if (excludedCount > 0) {
          console.log(
            `[Copy] ${excludedCount} item(s) excluded during copy to ${tab} for lighting`,
          );
        }

        return updateTechParams(
          previousSubproject,
          tab,
          'lighting',
          () => filteredLightingList,
        );
      });
    },
    [lightingSystems],
  );

  const getCostAndEnergyMetrics = useCallback(
    (tab: Tab) => {
      const alternative = subproject?.[tab];
      const params = alternative?.technologyParams['lighting']?.params;
      if (!params || !lightingSystems) {
        return undefined;
      }
      const metrics = params.reduce<{ energyDemand: number }>(
        (a, b) => {
          const lighting = lightingSystems.find(
            (l) => String(l.id) === b.techId,
          );
          if (!lighting) {
            console.error(
              'Unable to locate lighting unit, not found in storage',
            );
            return a;
          }
          a.energyDemand += Number(lighting.energyUsePerYr) * b.units;
          return a;
        },
        { energyDemand: 0 },
      );
      // Capital comes from the stored category total, never local arithmetic
      // -- see storedCategoryCapitalCost for why.
      const cost = storedCategoryCapitalCost(
        alternative,
        'lighting',
        costsArePending,
      );
      if (!cost && metrics.energyDemand === 0) {
        return undefined;
      }
      return { energyDemand: metrics.energyDemand, cost };
    },
    [subproject, lightingSystems, costsArePending],
  );

  const conventionalMetrics = useMemo(
    () => getCostAndEnergyMetrics('conventionalAlternative'),
    [getCostAndEnergyMetrics],
  );
  const reducedCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('reducedCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );
  const zeroCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('zeroCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }

  return (
    <DecarbPageLayout
      title={'select Lighting Components'}
      tabs={
        <DecarbTabsPanel
          currentTab={tab}
          onClick={setTab}
          conventionalMetrics={conventionalMetrics}
          reducedCarbonMetrics={reducedCarbonMetrics}
          zeroCarbonMetrics={zeroCarbonMetrics}
        />
      }
      footer={
        <DecarbFooterBar
          currentTab={tab}
          changeTab={setTab}
          saveSubproject={(onSuccessFn: () => void) => {
            if (pencilsDown) {
              onSuccessFn();
              return;
            }
            if (subproject && subId && selectedOrg) {
              const seq = ++saveSeqRef.current;
              safelySaveSubproject(
                subId,
                selectedOrg.id,
                subproject,
                queryClient,
              )
                .then((result) => {
                  adoptSaveResult(subproject, result, seq);
                  onSuccessFn();
                })
                .catch((error) => {
                  console.error('Failed to save lighting data:', error);
                  alert('Failed to save changes. Please try again.');
                });
            }
          }}
        />
      }
    >
      {subproject && lightingSystems && (
        <LightingForBuildingDetails
          subproject={subproject}
          costsArePending={costsArePending}
          tab={tab}
          lightingSystems={lightingSystems}
          onAddLighting={handleAddLighting}
          onChangeLightingCount={handleChangeLightingCount}
          onCopyConventionalAlternative={handleCopyLightingSettings}
          isLoading={isSaving}
          readOnly={pencilsDown}
          emptyText={
            pencilsDown ? undefined : (
              <HelperText
                tab={tab}
                onCopySelection={() => handleCopyLightingSettings(tab)}
              />
            )
          }
        />
      )}
    </DecarbPageLayout>
  );
};
